'use client';


import { round } from 'es-toolkit';
import { t } from 'mobx-state-tree';

import { UndoRedoActionType } from '../../core/undoRedo/undoRedoTypes';
import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { Instance } from 'mobx-state-tree';

/** Model for number property */
export const NumberProperty = t.compose('NumberProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Number),
    value: t.optional(t.number, 0),
    defaultValue: t.optional(t.number, 0),
    min: t.maybe(t.number),
    max: t.maybe(t.number),
    step: t.optional(t.number, 0.1),
    unit: t.maybe(t.string),
    decimalPlaces: t.maybe(t.number), // Clamps the value to specified decimal places i.e. use 0 for integer values
  })
).volatile(() => ({
  /** Snapshot of value taken at the start of a drag sequence; undefined when not dragging. */
  preDragValue: undefined as number | undefined,
})).actions(self => ({
  setDefaultValue(value: number) {
    if (self.min !== undefined) {
      value = Math.max(self.min, value);
    }
    if (self.max !== undefined) {
      value = Math.min(self.max, value);
    }
    if (self.decimalPlaces !== undefined) {
      value = round(value, self.decimalPlaces);
    }
    self.defaultValue = value;
  },
  setMin(min: number) {
    self.min = min;
  },
  setMax(max: number) {
    self.max = max;
  },
  setStep(step: number) {
    self.step = step;
  },
  setUnit(unit: string | undefined) {
    self.unit = unit;
  },
  setDecimalPlaces(decimalPlaces: number | undefined) {
    self.decimalPlaces = decimalPlaces;
  }
})).actions(self => ({
  setValue(value: number, fromPanel = true, initial = false, actionType: UndoRedoActionType = UndoRedoActionType.Primary) {
    let clampedValue = initial ? value : (self.min !== undefined ? Math.max(self.min, self.max !== undefined ? Math.min(self.max, value) : value) : value);
    if (self.decimalPlaces !== undefined) {
      clampedValue = round(clampedValue, self.decimalPlaces);
    }
    if (clampedValue === self.value) return;

    if (actionType === UndoRedoActionType.Silent) {
      // Capture the pre-drag baseline on the first Silent tick of a drag sequence.
      if (self.preDragValue === undefined) {
        self.preDragValue = self.value;
      }
      // Skip the MST write — no MobX reaction, no React re-render on every drag tick.
      self.notifyValueChange(self.path, clampedValue, { fromPanel, initial, actionType, property: self as Instance<typeof NumberProperty> });
      return;
    }

    // Primary/Secondary commit: include the pre-drag baseline so setPropertyValue
    // can record the correct undo value even though the engine already has the new value.
    const preDragValue = self.preDragValue;
    self.preDragValue = undefined;
    self.value = clampedValue;
    self.notifyValueChange(self.path, clampedValue, { fromPanel, initial, actionType, preDragValue, property: self as Instance<typeof NumberProperty> });
  }
})).views(self => ({
}));
